import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { adminProductsAPI } from '../services/api';
import { Link, useNavigate } from 'react-router-dom';
import CustomSelect from './CustomSelect';
// Uses global header via Layout

const AdminProducts = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category: '',
    stock_quantity: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (selectedCategory) params.category = selectedCategory;
      
      const response = await adminProductsAPI.getAll(params);
      setProducts(response.data.products);
      
      // Extract unique categories for filter
      const uniqueCategories = [...new Set(response.data.products.map(p => p.category))];
      setCategories(uniqueCategories);
    } catch (err) {
      setError('Failed to fetch products');
      console.error('Error fetching products:', err);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedCategory]);

  useEffect(() => {
    if (!isAdmin()) {
      setError('Access denied. Admin privileges required.');
      setLoading(false);
      return;
    }
    fetchProducts();
  }, [isAdmin, fetchProducts]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Update image preview
    if (name === 'image_url') {
      setImagePreview(value);
    }
  };

  const handleImageFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
      if (formErrors.imageFile) {
        setFormErrors((prev) => ({ ...prev, imageFile: '' }));
      }
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Product name is required';
    } else if (formData.name.length < 2) {
      errors.name = 'Product name must be at least 2 characters';
    } else if (formData.name.length > 100) {
      errors.name = 'Product name must be less than 100 characters';
    }
    
    if (!formData.description.trim()) {
      errors.description = 'Description is required';
    } else if (formData.description.length < 10) {
      errors.description = 'Description must be at least 10 characters';
    } else if (formData.description.length > 1000) {
      errors.description = 'Description must be less than 1000 characters';
    }
    
    if (!formData.price || parseFloat(formData.price) <= 0) {
      errors.price = 'Price must be greater than 0';
    }
    
    if (!formData.category.trim()) {
      errors.category = 'Category is required';
    } else if (formData.category.length < 2) {
      errors.category = 'Category must be at least 2 characters';
    } else if (formData.category.length > 50) {
      errors.category = 'Category must be less than 50 characters';
    }
    
    if (formData.stock_quantity === '' || parseInt(formData.stock_quantity) < 0) {
      errors.stock_quantity = 'Stock quantity must be 0 or greater';
    }
    
    if (!imageFile) {
      errors.imageFile = 'Product image is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setSuccessMessage('');
    
    try {
      await adminProductsAPI.create({ ...formData, image: imageFile });
      setFormData({
        name: '',
        description: '',
        price: '',
        category: '',
        stock_quantity: ''
      });
      setImageFile(null);
      setImagePreview('');
      setFormErrors({});
      setSuccessMessage('Product added successfully!');
      setShowAddForm(false);
      fetchProducts();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error adding product:', err);
      const apiErr = err.response?.data;
      setFormErrors({ submit: apiErr?.errors ? apiErr.errors.join(', ') : (apiErr?.error || 'Failed to add product') });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (product) => {
    navigate(`/admin/products/${product.id}/edit`);
  };

  const handleDelete = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await adminProductsAPI.delete(productId);
        fetchProducts();
      } catch (err) {
        console.error('Error deleting product:', err);
        alert('Failed to delete product');
      }
    }
  };

  const cancelAdd = () => {
    setShowAddForm(false);
    setFormData({
      name: '',
      description: '',
      price: '',
      category: '',
      stock_quantity: ''
    });
    setImageFile(null);
    setImagePreview('');
    setFormErrors({});
    setSuccessMessage('');
  };

  if (!isAdmin()) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
          <p className="text-gray-600">You need admin privileges to access this page.</p>
          <Link to="/dashboard" className="text-blue-600 hover:text-blue-800 mt-4 inline-block">
            Go to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Main Content */}
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Header */}
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Product Management</h2>
            <p className="text-gray-600">Manage your herbal products and inventory</p>
          </div>

          {/* Stock Alerts */}
          {products.some(p => p.stock_quantity === 0) && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    Stock Alert
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    <p>
                      {products.filter(p => p.stock_quantity === 0).length} product(s) are out of stock. 
                      Consider restocking these items.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {products.some(p => p.stock_quantity > 0 && p.stock_quantity <= 5) && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    Low Stock Alert
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>
                      {products.filter(p => p.stock_quantity > 0 && p.stock_quantity <= 5).length} product(s) have low stock. 
                      Consider restocking soon.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Search and Filter */}
          <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
            <div className="flex flex-col md:flex-row gap-4 mb-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
              <div className="md:w-48">
                <CustomSelect
                  id="category-filter"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  options={[
                    { value: '', label: 'All Categories' },
                    ...categories.map(category => ({
                      value: category,
                      label: category
                    }))
                  ]}
                  className=""
                />
              </div>
              <button
                onClick={() => setShowAddForm(true)}
                className="bg-green-600 text-white px-6 py-2 rounded-full text-sm font-medium hover:bg-green-700"
              >
                Add New Product
              </button>
            </div>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-center">
              <svg className="h-5 w-5 text-green-400 mr-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <p className="text-sm font-medium text-green-800">{successMessage}</p>
            </div>
          )}

          {/* Add/Edit Product Form */}
          {(showAddForm) && (
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6 md:p-8 mb-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    Add New Product
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">Fill in the details to add a new product to your inventory</p>
                </div>
                <button
                  type="button"
                  onClick={cancelAdd}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Close form"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {formErrors.submit && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center">
                  <svg className="h-5 w-5 text-red-400 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <p className="text-sm font-medium text-red-800">{formErrors.submit}</p>
                </div>
              )}

              <form onSubmit={handleAddProduct}>
                <div className="space-y-6">
                  {/* Basic Information Section */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                      Basic Information
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Product Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          maxLength={100}
                          placeholder="Enter product name"
                          className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors ${
                            formErrors.name 
                              ? 'border-red-300 bg-red-50' 
                              : 'border-gray-300 bg-white'
                          }`}
                        />
                        <div className="flex justify-between items-center mt-1">
                          {formErrors.name && (
                            <p className="text-xs text-red-600">{formErrors.name}</p>
                          )}
                          <p className="text-xs text-gray-500 ml-auto">
                            {formData.name.length}/100 characters
                          </p>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Category <span className="text-red-500">*</span>
                        </label>
                        {categories.length > 0 ? (
                          <CustomSelect
                            id="category-select"
                            value={formData.category}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === '__new__') {
                                const newCategory = prompt('Enter new category name:');
                                if (newCategory) {
                                  setFormData(prev => ({ ...prev, category: newCategory }));
                                }
                              } else {
                                setFormData(prev => ({ ...prev, category: value }));
                              }
                            }}
                            options={[
                              { value: '', label: 'Select or enter category' },
                              ...categories.map(cat => ({ value: cat, label: cat })),
                              { value: '__new__', label: '+ Add New Category' }
                            ]}
                            className={formErrors.category ? 'border-red-300' : ''}
                          />
                        ) : (
                          <input
                            type="text"
                            name="category"
                            value={formData.category}
                            onChange={handleInputChange}
                            required
                            maxLength={50}
                            placeholder="Enter category"
                            className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors ${
                              formErrors.category 
                                ? 'border-red-300 bg-red-50' 
                                : 'border-gray-300 bg-white'
                            }`}
                          />
                        )}
                        {formErrors.category && (
                          <p className="text-xs text-red-600 mt-1">{formErrors.category}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Pricing & Inventory Section */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                      Pricing & Inventory
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Price (₹) <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500">₹</span>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            name="price"
                            value={formData.price}
                            onChange={handleInputChange}
                            required
                            placeholder="0.00"
                            className={`w-full pl-8 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors ${
                              formErrors.price 
                                ? 'border-red-300 bg-red-50' 
                                : 'border-gray-300 bg-white'
                            }`}
                          />
                        </div>
                        {formErrors.price && (
                          <p className="text-xs text-red-600 mt-1">{formErrors.price}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Stock Quantity <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          min="0"
                          name="stock_quantity"
                          value={formData.stock_quantity}
                          onChange={handleInputChange}
                          required
                          placeholder="0"
                          className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors ${
                            formErrors.stock_quantity 
                              ? 'border-red-300 bg-red-50' 
                              : 'border-gray-300 bg-white'
                          }`}
                        />
                        {formErrors.stock_quantity && (
                          <p className="text-xs text-red-600 mt-1">{formErrors.stock_quantity}</p>
                        )}
                        {formData.stock_quantity && parseInt(formData.stock_quantity) <= 5 && (
                          <p className="text-xs text-yellow-600 mt-1">
                            ⚠️ Low stock warning will be shown
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Description Section */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                      Description
                    </h4>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Product Description <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        required
                        rows={4}
                        maxLength={1000}
                        placeholder="Enter detailed product description..."
                        className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors resize-none ${
                          formErrors.description 
                            ? 'border-red-300 bg-red-50' 
                            : 'border-gray-300 bg-white'
                        }`}
                      />
                      <div className="flex justify-between items-center mt-1">
                        {formErrors.description && (
                          <p className="text-xs text-red-600">{formErrors.description}</p>
                        )}
                        <p className="text-xs text-gray-500 ml-auto">
                          {formData.description.length}/1000 characters
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Image Section */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
                      Product Image
                    </h4>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Image File <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageFileChange}
                        className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors ${
                          formErrors.imageFile
                            ? 'border-red-300 bg-red-50' 
                            : 'border-gray-300 bg-white'
                        }`}
                      />
                      {formErrors.imageFile && (
                        <p className="text-xs text-red-600 mt-1">{formErrors.imageFile}</p>
                      )}
                      {(imagePreview || imageFile) && (
                        <div className="mt-4">
                          <p className="text-sm font-medium text-gray-700 mb-2">Image Preview:</p>
                          <div className="relative inline-block">
                            <img
                              src={imagePreview || URL.createObjectURL(imageFile)}
                              alt="Preview"
                              className="w-32 h-32 object-cover rounded-lg border-2 border-gray-200 shadow-sm"
                              onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/128x128?text=Invalid+URL';
                                e.target.className = 'w-32 h-32 object-cover rounded-lg border-2 border-red-200';
                              }}
                            />
                            {imageFile && !formErrors.imageFile && (
                              <div className="absolute top-1 right-1 bg-green-500 rounded-full p-1">
                                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={cancelAdd}
                    disabled={isSubmitting}
                    className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center"
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Adding...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add Product
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Products Table */}
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
              <p className="text-red-700">{error}</p>
              <button
                onClick={fetchProducts}
                className="mt-4 px-4 py-2 bg-red-600 text-white rounded-full hover:bg-red-700"
              >
                Try Again
              </button>
            </div>
          ) : products.length === 0 ? (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-12 text-center">
              <p className="text-gray-600 text-lg">No products found</p>
              <p className="text-gray-500">Try adjusting your search or filter criteria</p>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <div className="px-4 py-5 sm:p-6">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Product
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Category
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Price
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Stock Quantity
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {products.map((product) => (
                        <tr key={product.id} className={product.stock_quantity === 0 ? 'bg-red-50' : ''}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <img
                                src={product.image_url}
                                alt={product.name}
                                className="w-12 h-12 object-cover rounded-lg mr-4"
                                onError={(e) => {
                                  e.target.src = 'https://via.placeholder.com/48x48?text=No+Image';
                                }}
                              />
                              <div>
                                <div className="text-sm font-medium text-gray-900">{product.name}</div>
                                <div className="text-sm text-gray-500 truncate max-w-xs">
                                  {product.description}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {product.category}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {product.formatted_price}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <span className={`text-sm font-medium ${
                                product.stock_quantity === 0 ? 'text-red-600' : 
                                product.stock_quantity <= 5 ? 'text-yellow-600' : 'text-gray-900'
                              }`}>
                                {product.stock_quantity}
                              </span>
                              {product.stock_quantity === 0 && (
                                <span className="ml-2 text-xs text-red-500 font-medium">
                                  OUT OF STOCK
                                </span>
                              )}
                              {product.stock_quantity > 0 && product.stock_quantity <= 5 && (
                                <span className="ml-2 text-xs text-yellow-500 font-medium">
                                  LOW STOCK
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              product.stock_quantity === 0 
                                ? 'bg-red-100 text-red-800' 
                                : product.stock_quantity <= 5 
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-green-100 text-green-800'
                            }`}>
                              {product.stock_quantity === 0 
                                ? 'Out of Stock' 
                                : product.stock_quantity <= 5 
                                  ? 'Low Stock'
                                  : 'In Stock'
                              }
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleEdit(product)}
                                className="text-blue-600 hover:text-blue-900"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDelete(product.id)}
                                className="text-red-600 hover:text-red-900"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminProducts;
